import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';

const FILTER_TYPES = [
  { value: 'batch', label: 'Batch Name' },
  { value: 'whatsapp', label: 'WhatsApp Group Name' },
];

function calculateMonthsPassed(dateString) {
  if (!dateString) return '';
  const start = new Date(dateString);
  if (Number.isNaN(start.getTime())) return '';

  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());

  if (now.getDate() < start.getDate()) {
    months -= 1;
  }

  if (months < 0) return '0';
  return String(months);
}

const COLUMN_DEFINITIONS = [
  { id: 'studentId', label: 'Student ID', getValue: (row) => row.studentId || '', width: 14 },
  { id: 'fullName', label: 'Full Name', getValue: (row) => row.fullName || '', width: 28 },
  { id: 'nicNumber', label: 'NIC', getValue: (row) => row.nicNumber || '', width: 14 },
  { id: 'telephone', label: 'Telephone', getValue: (row) => row.telephone || '', width: 14 },
  { id: 'email', label: 'Email', getValue: (row) => row.email || '', width: 26 },
  { id: 'dob', label: 'DOB', getValue: (row) => row.dob || '', width: 12 },
  { id: 'gender', label: 'Gender', getValue: (row) => row.gender || '', width: 10 },
  { id: 'address', label: 'Address', getValue: (row) => row.address || '', width: 32 },
  { id: 'courseName', label: 'Course', getValue: (row) => row.courseName || '', width: 26 },
  { id: 'courseShortName', label: 'Course Code', getValue: (row) => row.courseShortName || '', width: 14 },
  { id: 'batchName', label: 'Batch', getValue: (row) => row.batchName || '', width: 20 },
  { id: 'batchInternalName', label: 'Batch Internal Name', getValue: (row) => row.batchInternalName || '', width: 22 },
  { id: 'whatsappGroupName', label: 'WhatsApp Group', getValue: (row) => row.whatsappGroupName || '', width: 24 },
  { id: 'registrationNumber', label: 'Registration No', getValue: (row) => row.registrationNumber || '', width: 18 },
  { id: 'registrationStatus', label: 'Registration Status', getValue: (row) => row.registrationStatus || '', width: 18 },
  { id: 'deliveryStatus', label: 'Delivery Status', getValue: (row) => row.deliveryStatus || '', width: 18 },
  { id: 'stream', label: 'Stream', getValue: (row) => row.stream || '', width: 12 },
  { id: 'universityID', label: 'University ID', getValue: (row) => row.universityID || '', width: 18 },
  {
    id: 'universityRegisterDate',
    label: 'University Reg Date',
    getValue: (row) => row.universityRegisterDate || '',
    width: 14,
  },
  {
    id: 'monthsPassed',
    label: 'Number of Months Passed',
    getValue: (row) => row.monthsPassed || '',
    width: 18,
  },
  {
    id: 'nameVerification',
    label: 'Name Verification',
    getValue: (row) => row.nameVerification || '',
    width: 18,
  },
];

function buildReportRows(students, filterType, rawFilterValue) {
  const filterValue = String(rawFilterValue || '').trim().toLowerCase();
  if (!filterValue) return [];

  const rows = [];

  (students || []).forEach((student) => {
    const regs = Array.isArray(student.registrations) ? student.registrations : [];
    regs.forEach((reg) => {
      const batchName = (reg.batchName || reg.batchInternalName || '').trim();
      const whatsapp = (reg.whatsappGroupName || '').trim();
      const universityRegDate = reg.universityRegisterDate
        ? String(reg.universityRegisterDate).slice(0, 10)
        : '';

      const batchMatch = batchName.toLowerCase().includes(filterValue);
      const whatsappMatch = whatsapp.toLowerCase().includes(filterValue);

      if (filterType === 'batch' && !batchMatch) return;
      if (filterType === 'whatsapp' && !whatsappMatch) return;

      rows.push({
        studentId: student.studentId || '',
        fullName: student.fullName || '',
        email: student.email || '',
        telephone: student.telephone || '',
        nicNumber: student.nicNumber || '',
        dob: student.dob || '',
        gender: student.gender || '',
        address: student.address || '',
        courseName: reg.courseName || '',
        courseShortName: reg.courseShortName || '',
        batchName,
        batchInternalName: reg.batchInternalName || '',
        whatsappGroupName: whatsapp,
        registrationNumber: reg.registrationNumber || '',
        registrationStatus: reg.registrationStatus || '',
        deliveryStatus: reg.deliveryStatus || '',
        stream: reg.stream || '',
        universityID: reg.universityID || '',
        universityRegisterDate: universityRegDate,
        monthsPassed: calculateMonthsPassed(universityRegDate),
        nameVerification: reg.nameVerification || '',
      });
    });
  });

  return rows;
}

export default function StudentDetailReport() {
  const [filterType, setFilterType] = useState('batch');
  const [filterValue, setFilterValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [selectedColumnIds, setSelectedColumnIds] = useState(COLUMN_DEFINITIONS.map((c) => c.id));

  const hasData = reportRows.length > 0;
  const selectedColumns = COLUMN_DEFINITIONS.filter((c) => selectedColumnIds.includes(c.id));

  const toggleColumn = (id) => {
    setSelectedColumnIds((prev) =>
      prev.includes(id) ? prev.filter((colId) => colId !== id) : [...prev, id]
    );
  };

  const handleSelectAllColumns = () => {
    setSelectedColumnIds(COLUMN_DEFINITIONS.map((c) => c.id));
  };

  const handleClearAllColumns = () => {
    setSelectedColumnIds([]);
  };

  const handleGenerate = async () => {
    const trimmed = filterValue.trim();
    if (!trimmed) {
      Swal.fire('Filter required', 'Please enter a batch name or WhatsApp group name.', 'info');
      return;
    }

    try {
      setLoading(true);
      setStudents([]);
      setReportRows([]);

      const params = {
        page: 1,
        perPage: 500,
        deliveryStatus: 'all',
        search: trimmed,
      };

      const response = await axiosInstance.get('/admin/students', { params });
      const apiStudents = response?.data?.students || [];

      const rows = buildReportRows(apiStudents, filterType, trimmed);

      if (!rows.length) {
        Swal.fire(
          'No results',
          'No students found for the selected filter. Try a different value.',
          'info'
        );
      }

      setStudents(apiStudents);
      setReportRows(rows);
    } catch (error) {
      console.error('Failed to generate student detail report:', error);
      Swal.fire('Error', 'Failed to load student details. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!hasData) {
      Swal.fire('No data', 'Generate a report before downloading Excel.', 'info');
      return;
    }

    if (!selectedColumns.length) {
      Swal.fire('No columns selected', 'Please select at least one column to include in the report.', 'info');
      return;
    }

    try {
      const headerRows = [];
      headerRows.push(['Student Detail Report']);
      headerRows.push(['Generated At', new Date().toLocaleString()]);
      const filterLabel = FILTER_TYPES.find((f) => f.value === filterType)?.label || 'Filter';
      headerRows.push([filterLabel, filterValue.trim()]);
      headerRows.push([]);

      const tableHeader = selectedColumns.map((col) => col.label);

      const tableRows = reportRows.map((row) =>
        selectedColumns.map((col) => col.getValue(row))
      );

      const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, tableHeader, ...tableRows]);
      worksheet['!cols'] = selectedColumns.map((col) => ({ wch: col.width || 18 }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Student Detail');

      const safeFilter = filterValue.trim().replace(/\s+/g, '_') || 'All';
      const today = new Date().toISOString().split('T')[0];
      const fileName = `Student_Detail_Report_${filterType}_${safeFilter}_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Excel download failed:', error);
      Swal.fire('Download failed', 'Unable to create the Excel report. Please try again.', 'error');
    }
  };

  const handleDownloadPdf = () => {
    if (!hasData) {
      Swal.fire('No data', 'Generate a report before downloading PDF.', 'info');
      return;
    }

    if (!selectedColumns.length) {
      Swal.fire('No columns selected', 'Please select at least one column to include in the report.', 'info');
      return;
    }

    const filterLabel = FILTER_TYPES.find((f) => f.value === filterType)?.label || 'Filter';
    const now = new Date();
    const generatedAt = now.toLocaleString();

    const escapeHtml = (value) =>
      String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const rowsHtml = reportRows
      .map((row, index) => `
        <tr>
          <td>${index + 1}</td>
          ${selectedColumns
            .map((col) => `<td>${escapeHtml(col.getValue(row))}</td>`)
            .join('')}
        </tr>
      `)
      .join('');

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Student Detail Report</title>
          <style>
            body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #111827; }
            h1 { font-size: 24px; margin-bottom: 4px; }
            .meta { font-size: 12px; color: #6b7280; margin-bottom: 16px; }
            .meta span { display: inline-block; margin-right: 16px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            thead tr { background: #f3f4f6; }
            th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
            th { font-weight: 600; }
            tbody tr:nth-child(even) { background: #f9fafb; }
          </style>
        </head>
        <body>
          <h1>Student Detail Report</h1>
          <div class="meta">
            <span><strong>${escapeHtml(filterLabel)}:</strong> ${escapeHtml(filterValue)}</span>
            <span><strong>Generated At:</strong> ${escapeHtml(generatedAt)}</span>
            <span><strong>Total Rows:</strong> ${reportRows.length}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                ${selectedColumns
                  .map((col) => `<th>${escapeHtml(col.label)}</th>`)
                  .join('')}
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      Swal.fire('Popup blocked', 'Allow popups for this site to download the PDF.', 'warning');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    // Give the browser a moment to render before printing
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  return (
    <DashboardLayout title="Student Detail Report">
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          {/* Header */}
          <div className="rounded-2xl bg-white p-8 shadow-lg border border-slate-200/60 backdrop-blur-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/30">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-white">
                      <path fillRule="evenodd" d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875Zm6 16.5c.66 0 1.277-.19 1.797-.518l1.048 1.048a.75.75 0 0 0 1.06-1.06l-1.047-1.048A3.375 3.375 0 1 0 11.625 18Zm0-1.5a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75Z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Student Detail Report</h1>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">
                        Analytics
                      </span>
                      <span className="text-slate-400">•</span>
                      <span className="text-sm text-slate-500">Professional Reporting</span>
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-2xl">
                  Generate comprehensive student detail reports organized by batch or WhatsApp group. 
                  Export data in multiple formats for analysis and record-keeping.
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd" />
                </svg>
                <span>Last updated: Just now</span>
              </div>
            </div>
          </div>

          {/* Filters + Actions */}
          <div className="rounded-2xl bg-white shadow-lg border border-slate-200/60 overflow-hidden">
            {/* Filter Section */}
            <div className="bg-gradient-to-r from-slate-50 to-white p-6 border-b border-slate-200">
              <div className="grid gap-6 lg:grid-cols-12">
                {/* Filter Type */}
                <div className="lg:col-span-3">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
                    Filter By
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-indigo-500">
                        <path d="M3.75 3A.75.75 0 0 0 3 3.75v1.5c0 .199.079.39.22.53l4.53 4.53v3.94c0 .199.079.39.22.53l2 2A.75.75 0 0 0 11 16.5V10.31l4.53-4.53A.75.75 0 0 0 16 5.25v-1.5A.75.75 0 0 0 15.25 3H3.75Z" />
                      </svg>
                    </div>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="w-full rounded-xl border-2 border-slate-200 bg-white pl-10 pr-4 py-3 text-sm font-semibold text-slate-800 shadow-sm hover:border-indigo-300 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
                    >
                      {FILTER_TYPES.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Choose grouping method</p>
                </div>

                {/* Filter Value */}
                <div className="lg:col-span-6">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
                    {filterType === 'batch' ? 'Batch Name (or part of name)' : 'WhatsApp Group Name (or part of name)'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-slate-400">
                        <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                      placeholder={
                        filterType === 'batch'
                          ? 'e.g., HNDIT - WEP JAN 2024'
                          : 'e.g., HNDIT JAN 2024 WhatsApp'
                      }
                      className="w-full rounded-xl border-2 border-slate-200 bg-white pl-10 pr-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 hover:border-indigo-300 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Type partial or complete name to filter results
                  </p>
                </div>

                {/* Generate Button */}
                <div className="lg:col-span-3 flex items-end">
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-indigo-500/40 hover:shadow-xl hover:shadow-indigo-500/50 hover:from-indigo-700 hover:to-violet-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    {loading ? (
                      <>
                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="h-5 w-5"
                        >
                          <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM6.75 9.25a.75.75 0 0 0 0 1.5h4.59l-2.1 1.95a.75.75 0 0 0 1.02 1.1l3.5-3.25a.75.75 0 0 0 0-1.1l-3.5-3.25a.75.75 0 1 0-1.02 1.1l2.1 1.95H6.75Z" clipRule="evenodd" />
                        </svg>
                        <span>Generate Report</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Export Actions */}
            <div className="bg-slate-50/50 px-6 py-4">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-500">
                      <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">Export Options</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Download comprehensive reports in Excel format or print to PDF for sharing
                    </p>
                    <div className="mt-3">
                      <p className="text-[11px] font-semibold text-slate-600 mb-1">Columns to include</p>
                      <div className="flex flex-wrap gap-2">
                        {COLUMN_DEFINITIONS.map((col) => (
                          <label
                            key={col.id}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] text-slate-700 cursor-pointer hover:border-indigo-300 hover:text-indigo-700"
                          >
                            <input
                              type="checkbox"
                              className="h-3 w-3 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                              checked={selectedColumnIds.includes(col.id)}
                              onChange={() => toggleColumn(col.id)}
                            />
                            <span>{col.label}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                        <button
                          type="button"
                          onClick={handleSelectAllColumns}
                          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-semibold text-slate-600 hover:border-indigo-300 hover:text-indigo-700"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleClearAllColumns}
                          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-semibold text-slate-600 hover:border-rose-300 hover:text-rose-700"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleDownloadExcel}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-white shadow-md shadow-emerald-600/30 hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-600/40 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all transform hover:scale-105 active:scale-95"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="h-4 w-4"
                    >
                      <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 0 0 3 3.5v13A1.5 1.5 0 0 0 4.5 18h11a1.5 1.5 0 0 0 1.5-1.5V7.621a1.5 1.5 0 0 0-.44-1.06l-4.12-4.122A1.5 1.5 0 0 0 11.378 2H4.5Zm2.25 8.5a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Zm0 3a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Z" clipRule="evenodd" />
                    </svg>
                    <span>Download Excel</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-white shadow-md shadow-rose-600/30 hover:bg-rose-700 hover:shadow-lg hover:shadow-rose-600/40 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 transition-all transform hover:scale-105 active:scale-95"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="h-4 w-4"
                    >
                      <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 0 0 3 3.5v13A1.5 1.5 0 0 0 4.5 18h11a1.5 1.5 0 0 0 1.5-1.5V7.621a1.5 1.5 0 0 0-.44-1.06l-4.12-4.122A1.5 1.5 0 0 0 11.378 2H4.5ZM9 8a.75.75 0 0 1 .75.75v.69l.22-.22a.75.75 0 1 1 1.06 1.06l-1.5 1.5a.75.75 0 0 1-1.06 0l-1.5-1.5a.75.75 0 1 1 1.06-1.06l.22.22V8.75A.75.75 0 0 1 9 8Zm.75 4.5a.75.75 0 0 0-1.5 0v1.5a.75.75 0 0 0 1.5 0v-1.5Z" clipRule="evenodd" />
                    </svg>
                    <span>Print / PDF</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Report Table */}
          <div className="rounded-2xl bg-white shadow-lg border border-slate-200/60 overflow-hidden">
            {!hasData && !loading && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="mb-6 relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full blur-xl opacity-20 animate-pulse" />
                  <div className="relative inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-100">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-10 w-10 text-indigo-400"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875Zm5.845 17.03a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72V12a.75.75 0 0 0-1.5 0v4.19l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">No Report Generated Yet</h3>
                <p className="max-w-md text-sm text-slate-600 leading-relaxed">
                  Select your filter criteria above and click
                  <span className="font-semibold text-indigo-600"> Generate Report </span>
                  to view detailed student information.
                </p>
                <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-4 h-4 text-emerald-500"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>Excel Export</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-4 h-4 text-rose-500"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>PDF Print</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-4 h-4 text-indigo-500"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>Detailed Insights</span>
                  </div>
                </div>
              </div>
            )}

            {hasData && (
              <>
                {/* Table Header */}
                <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-4 border-b border-slate-200">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="w-5 h-5"
                        >
                          <path d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM1.49 15.326a.78.78 0 0 1-.358-.442 3 3 0 0 1 4.308-3.516 6.484 6.484 0 0 0-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 0 1-2.07-.655ZM16.44 15.98a4.97 4.97 0 0 0 2.07-.654.78.78 0 0 0 .357-.442 3 3 0 0 0-4.308-3.517 6.484 6.484 0 0 1 1.907 3.96 2.32 2.32 0 0 1-.026.654ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5.304 16.19a.844.844 0 0 1-.277-.71 5 5 0 0 1 9.947 0 .843.843 0 0 1-.277.71A6.975 6.975 0 0 1 10 18a6.974 6.974 0 0 1-4.696-1.81Z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">Report Results</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Showing
                          <span className="font-semibold text-slate-700"> {reportRows.length} </span>
                          student registration(s)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-200">
                        <span className="text-slate-500">Filter:</span>
                        <span className="font-semibold text-slate-700">
                          {filterType === 'batch' ? 'Batch Name' : 'WhatsApp Group'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="w-4 h-4 text-indigo-600"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 2.5c-1.31 0-2.526.386-3.546 1.051a.75.75 0 0 1-.82-1.256A8 8 0 0 1 18 9a22.47 22.47 0 0 1-1.228 7.351.75.75 0 1 1-1.417-.49A20.97 20.97 0 0 0 16.5 9 6.5 6.5 0 0 0 10 2.5ZM4.333 4.416a.75.75 0 0 1 .218 1.038A6.466 6.466 0 0 0 3.5 9c0 1.302.382 2.515 1.042 3.534l-1.04 1.04a.75.75 0 0 1-1.061-1.06l6.5-6.5a.75.75 0 0 1 1.06 1.06L8.903 8.173c.448-.196.93-.328 1.438-.392a.75.75 0 1 1 .123 1.494 5.018 5.018 0 0 0-3.766 1.456l-3.923 3.923a8.004 8.004 0 0 1-1.548-4.735c0-1.31.315-2.547.875-3.64a.75.75 0 0 1 1.038-.218Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="font-semibold text-indigo-700">{filterValue.trim()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table Content */}
                <div className="overflow-x-auto">
                  <div className="max-h-[600px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-700 border-b-2 border-slate-200">
                            #
                          </th>
                          {selectedColumns.map((col) => (
                            <th
                              key={col.id}
                              className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-700 border-b-2 border-slate-200"
                            >
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-100">
                        {reportRows.map((row, index) => (
                          <tr
                            key={`${row.studentId}-${row.registrationNumber}-${index}`}
                            className="hover:bg-slate-50 transition-colors"
                          >
                            <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-600 font-medium">{index + 1}</td>
                            {selectedColumns.map((col) => (
                              <td
                                key={col.id}
                                className="whitespace-nowrap px-4 py-3 text-xs text-slate-700"
                              >
                                {col.getValue(row)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Table Footer */}
                <div className="bg-slate-50 px-6 py-3 border-t border-slate-200">
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <p>
                      Total: <span className="font-bold text-slate-900">{reportRows.length}</span> records
                    </p>
                    <p className="text-slate-500">
                      Generated on
                      {new Date().toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              </>
            )}

            {loading && (
              <div className="flex items-center justify-center py-6 text-xs text-slate-500">
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-500" />
                Loading students and building report...
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
